
import React, { useState, useEffect, useRef } from "react";
import "./Support.css";

const Support = () => {
  const [currentPage, setCurrentPage] = useState("support");
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [ticketType, setTicketType] = useState("");
  const [tickets, setTickets] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const popupRef = useRef(null);

  const userId = localStorage.getItem("userId");

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTogglePopup = () => {
    setShowPopup(!showPopup);
  };

  const handleClickOutside = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setShowPopup(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch tickets for user
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await fetch(
          `https://backend.maamaas.com/food/tickets/user/${userId}`
        );
        const data = await response.json();
        setTickets(Array.isArray(data) ? data : data.tickets || []);
      } catch (error) {
        console.error("Error fetching tickets:", error);
        setTickets([]);
      }
    };

    if (userId) {
      fetchTickets();
    }
  }, [userId]);

  const faqs = [
    { name: "Coupons & Offers", page: "coupons" },
    { name: "Orders/Products Related", page: "orders" },
    { name: "Feedback & Suggestions", page: "feedback" },
  ];

  const couponQuestions = [
    {
      question: "Coupon not working/expired coupon",
      answer: "Please check the coupon terms and validity before applying.",
    },
    {
      question: "I forgot to apply my coupon code. What do I do now?",
      answer: "Unfortunately, we can't apply coupons after order placement.",
    },
    {
      question: "How do I refer my friend?",
      answer: "Go to the refer section in the app and follow the instructions.",
    },
  ];

  const orderQuestions = [
    {
      question: "Can I reschedule my order?",
      answer: "If you've already placed an order, cancel and place a new one when available.",
    },
    {
      question: "Can I edit my cart/add items?",
      answer: "We start packing your order right away. So changes after placing the order aren't allowed.",
    },
    {
      question: "Is there a minimum order value?",
      answer: "No, we do not have a minimum or maximum order value.",
    },
  ];

  const feedbackQuestions = [
    {
      question: "Out of stock?",
      answer: "We apologize for the inconvenience. Please check back later or try alternative products.",
    },
    {
      question: "Packing feedback?",
      answer: "We value your feedback on our packaging. Please share specific details so we can improve.",
    },
    {
      question: "Any feedback/Review?",
      answer: "We'd love to hear from you! Please provide your feedback through our app or website.",
    },
  ];

  const toggleQuestion = (q) => {
    if (expandedQuestion === q) {
      setExpandedQuestion(null);
    } else {
      setExpandedQuestion(q);
    }
  };

  const handleSendMessage = async () => {
    const userId = localStorage.getItem("userId");

    if (!userId || !ticketType || !chatMessage) {
      alert("Please fill all required fields");
      return;
    }

    const sendTicket = async (base64) => {
      const payload = {
        userId: parseInt(userId),
        ticketType,
        status: "OPEN",
        message: chatMessage,
        attachmentUrl: base64 || null,
      };

      try {
        const response = await fetch(
          "https://backend.maamaas.com/food/tickets/create",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          alert("Ticket submitted successfully");
          setChatMessage("");
          setSelectedFile(null);
          setTicketType("");
          setShowChatPopup(false);
        } else {
          alert("Failed to submit ticket");
        }
      } catch (error) {
        console.error("Error submitting ticket:", error);
        alert("Error while sending ticket");
      }
    };

    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        sendTicket(base64);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      sendTicket(null);
    }
  };

  const thStyle = { border: "1px solid #ddd", padding: "8px" };
  const tdStyle = { border: "1px solid #ddd", padding: "8px" };

  const renderTicketTable = () => (
    <div style={{ marginTop: "20px", padding: "10px", overflowX: "auto" }}>
      <h4 style={{ marginBottom: "10px", fontWeight: "bold" }}>
        Your Tickets
      </h4>
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        minWidth: isMobile ? "600px" : "100%"
      }}>
        <thead>
          <tr style={{ backgroundColor: "#b15dc6", color: "#fff" }}>
            <th style={thStyle}>Ticket ID</th>
            <th style={thStyle}>Ticket Type</th>
            <th style={thStyle}>Message</th>
            <th style={thStyle}>Created Date</th>
            <th style={thStyle}>Resolved Date</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Admin Response</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket, index) => (
            <tr
              key={ticket.id}
              style={{
                backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
              }}
            >
              <td style={tdStyle}>{ticket.id}</td>
              <td style={tdStyle}>{ticket.ticketType}</td>
              <td style={tdStyle}>{ticket.message}</td>
              <td style={tdStyle}>
                {new Date(ticket.createdAt).toLocaleString()}
              </td>
              <td style={tdStyle}>
                {ticket.resolvedAt
                  ? new Date(ticket.resolvedAt).toLocaleString()
                  : "Pending"}
              </td>
              <td
                style={{
                  ...tdStyle,
                  color:
                    ticket.status === "RESOLVED"
                      ? "green"
                      : ticket.status === "REJECTED"
                        ? "red"
                        : "orange",
                  fontWeight: "bold",
                }}
              >
                {ticket.status}
              </td>
              <td style={tdStyle}>{ticket.adminResponse || "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderQuestions = (questions, title) => (
    <div style={{ marginTop: "30px" }}>
      <h3
        style={{
          fontWeight: "bold",
          marginBottom: "20px",
          background: "#b15dc6",
          color: "#fff",
          height: "40px",
          borderRadius: "20px",
          paddingTop: "5px",
          paddingLeft: "10px",
        }}
      >
        {title}
      </h3>
      {questions.map((item, index) => (
        <div key={index} style={{ marginBottom: "10px" }}>
          <h5
            style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            onClick={() => toggleQuestion(item.question)}
          >
            {item.question}
            <span style={{ fontWeight: "bold", fontSize: "18px" }}>
              {expandedQuestion === item.question ? "−" : "+"}
            </span>
          </h5>
          {expandedQuestion === item.question && (
            <p style={{ marginTop: "5px", paddingLeft: "10px", color: "#444" }}>
              {item.answer}
            </p>
          )}
          <hr className="divider" />
        </div>
      ))}
    </div>
  );

  const renderPage = () => {
    switch (currentPage) {
      case "coupons":
        return renderQuestions(couponQuestions, "Coupons & Offers");
      case "orders":
        return renderQuestions(orderQuestions, "Orders/Products Related");
      case "feedback":
        return renderQuestions(feedbackQuestions, "Feedback & Suggestions");
      default:
        return null;
    }
  };

  return (
    <div className="support-container" style={{ marginTop: "30px", padding: isMobile ? "10px" : "0" }}>
      {currentPage === "support" ? (
        <div className="faqs-section">
          <h4
            className="faqs"
            style={{
              background: "rgb(177 93 198)",
              width: "75px",
              color: "#fff",
              borderRadius: "4px",
              paddingLeft: "5px",
              fontFamily: "sans-serif",
            }}
          >
            FAQs
          </h4>
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                marginTop: "20px",
                background: "#b15cd6",
                color: "#fff",
                height: "40px",
                borderRadius: "10px",
                cursor: "pointer",
              }}
              onClick={() => setCurrentPage(faq.page)}
            >
              <p
                style={{ fontWeight: "500", marginLeft: "20px", paddingTop: "8px" }}
                className="Coupons"
              >
                {faq.name}
              </p>
            </div>
          ))}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "30px",
            }}
          >
            <button className="morebutton">Need More Help</button>
          </div>

          {/* Responsive Button Section */}
          <div
            style={{
              display: isMobile ? "block" : "flex",
              justifyContent: isMobile ? "flex-start" : "space-evenly",
              marginTop: "30px",
              cursor: "pointer",
              gap: isMobile ? "15px" : "0",
              textAlign: isMobile ? "left" : "left", // Changed from "center" to "left"
              paddingLeft: isMobile ? "20px" : "0",
            }}
          >
            <span
              onClick={() => setShowChatPopup(true)}
              style={{
                display: isMobile ? "block" : "inline",
                marginBottom: isMobile ? "15px" : "0"
              }}
            >
              <p className="chatwith">💬 Raise A Ticket</p>
            </span>
            <span
              onClick={handleTogglePopup}
              style={{
                cursor: "pointer",
                display: isMobile ? "block" : "inline"
              }}
            >
              <p className="chatwith">📞 Talk with us</p>
            </span>

            {showPopup && (
              <div
                ref={popupRef}
                style={{
                  position: "absolute",
                  top: isMobile ? "auto" : "480px",
                  bottom: isMobile ? "100px" : "auto",
                  right: isMobile ? "20px" : "460px",
                  left: isMobile ? "20px" : "auto",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "10px 15px",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                  zIndex: 1000,
                  textAlign: "center",
                }}
              >
                <p style={{ margin: 0, fontWeight: "bold" }}>
                  📱 +91-9876543210
                </p>
              </div>
            )}
          </div>

          {renderTicketTable()}
        </div>
      ) : (
        <div>
          <button
            onClick={() => setCurrentPage("support")}
            className="morebutton"
            style={{ marginBottom: "20px" }}
          >
            Back to FAQs
          </button>
          {renderPage()}
        </div>
      )}

      {/* Raise Ticket Popup - Responsive */}
      {showChatPopup && (
        <div
          style={{
            position: "fixed",
            bottom: isMobile ? "0" : "50px",
            right: isMobile ? "0" : "20px",
            width: isMobile ? "100%" : "300px",
            height: isMobile ? "auto" : "auto",
            maxHeight: isMobile ? "70vh" : "none",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: isMobile ? "10px 10px 0 0" : "10px",
            padding: "15px",
            boxShadow: "0 0 10px rgba(0,0,0,0.2)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <h4>Raise a Ticket</h4>
            <button
              onClick={() => setShowChatPopup(false)}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              ✖
            </button>
          </div>

          <label style={{ fontWeight: "bold" }}>Ticket Type:</label>
          <select
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "5px",
              marginBottom: "10px",
              border: "1px solid #ccc",
            }}
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
          >
            <option value="">--Select Type--</option>
            <option value="DELIVERY_ISSUE">Delivery Issue</option>
            <option value="PAYMENT_PROBLEM">Payment Problem</option>
            <option value="WRONG_ORDER">Wrong Order</option>
            <option value="SERVICE_QUALITY">Service Quality</option>
            <option value="OTHER">Other</option>
          </select>

          <textarea
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            rows="3"
            placeholder="Type your message..."
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "5px",
              border: "1px solid #ccc",
              resize: "vertical"
            }}
          />
          <input
            type="file"
            onChange={(e) => setSelectedFile(e.target.files[0])}
            style={{ marginTop: "10px", marginBottom: "10px", width: "100%" }}
          />
          <button
            onClick={handleSendMessage}
            className="morebutton"
            style={{ width: "100%" }}
          >
            Send
          </button>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          bottom: "0px",
          left: "0px",
          height: "30px",
          width: "100%",
          backgroundColor: "white",
          color: "black",
          textAlign: "center",
          padding: "5px",
          fontWeight: "bold",
          fontSize: "14px",
          boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.1)",
          zIndex: "999",
        }}
      >
        @Envision Integrated Services Pvt Ltd - Empowering Your Future 🚀
      </div>
    </div>
  );
};

export default Support;